import React, { useEffect, useState } from "react";

import { TrendingUp, Clock, UserCheck, Sparkles, AlertTriangle, GitCompareArrows } from "lucide-react";
import { useOutletContext } from "react-router-dom";

import PermissionGuard from "@/permissions/PermissionGuard";
import { PERMISSIONS } from "@/permissions/constant";

import { AdminStatisticsService } from "@/services/adminStatisticsService";
import { KpiTile } from "./components/StatWidgets";
import ConversionFunnel from "./components/ConversionFunnel";
import VelocityPanel from "./components/VelocityPanel";
import AiBandOutcome from "./components/AiBandOutcome";
import RejectionsByStage from "./components/RejectionsByStage";
import JobsAttentionPanel from "./components/JobsAttentionPanel";

// Admin Statistics — a DIAGNOSTIC recruitment-analytics page (not a volume dump;
// those counters live on the recruitment dashboard). It answers three executive
// questions with pre-computed insights so the reader gets the conclusion:
//   1. Where do we leak?  → Conversion Funnel
//   2. Are we slow?       → Speed & Velocity
//   3. Is our (and the AI's) judgment sound? → AI band vs outcome
// plus two actionable worklists (rejections-by-stage, jobs-needing-attention).
//
// Layout is space-dense on purpose (the brief: "scroll less, use space smartly"):
// a compact hero KPI strip above the fold from a single /overview call, then
// paired widgets in a responsive grid, then the full-width worklist.

const fmtPct = (v) => (v == null ? null : Math.round(v));
const fmtDays = (v) => (v == null ? null : v % 1 === 0 ? v : v.toFixed(1));

const AdminStatistics = () => {
  const setHeader = useOutletContext();

  const [funnel, setFunnel] = useState(null);
  const [funnelLoading, setFunnelLoading] = useState(true);
  const [velocity, setVelocity] = useState(null);
  const [velocityLoading, setVelocityLoading] = useState(true);
  const [ai, setAi] = useState(null);
  const [aiLoading, setAiLoading] = useState(true);
  const [rejections, setRejections] = useState(null);
  const [rejectionsLoading, setRejectionsLoading] = useState(true);
  const [attention, setAttention] = useState(null);
  const [attentionLoading, setAttentionLoading] = useState(true);

  useEffect(() => {
    setHeader({ title: "Statistics", subTitle: "Recruitment Diagnostics" });
    return () => setHeader({ title: "", subTitle: "" });
  }, [setHeader]);

  useEffect(() => {
    let active = true;

    // Each widget fetches independently with a mounted-guard so one slow or
    // failed call never blocks the others; each degrades to an empty state.
    const load = (fetcher, setData, setLoading) => {
      setLoading(true);
      fetcher()
        .then((data) => {
          if (active) setData(data ?? null);
        })
        .catch(() => {
          if (active) setData(null);
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    };

    load(() => AdminStatisticsService.getFunnel(), setFunnel, setFunnelLoading);
    load(() => AdminStatisticsService.getVelocity(), setVelocity, setVelocityLoading);
    load(() => AdminStatisticsService.getAiRecommendations(), setAi, setAiLoading);
    load(() => AdminStatisticsService.getRejections(), setRejections, setRejectionsLoading);
    load(() => AdminStatisticsService.getJobsAttention(), setAttention, setAttentionLoading);

    return () => {
      active = false;
    };
  }, []);

  // Read off the four widget responses this page already loaded, not from a
  // sixth request. `/overview` computes these same five figures by running
  // the funnel, velocity, AI and attention queries itself, so firing it
  // beside the widgets ran every one of those queries twice for one paint —
  // and made the strip a separate read that could disagree with the chart
  // directly beneath it. Each tile also arrives as soon as its own widget
  // does, rather than waiting on the slowest of the four.
  const kpis = [
    {
      label: "Applied → Hired",
      value: fmtPct(funnel?.overallConversion),
      unit: funnel?.overallConversion == null ? "" : "%",
      sub: funnel?.biggestDropOffLabel ? `Leak: ${funnel.biggestDropOffLabel}` : "End-to-end conversion",
      color: "#6366f1",
      icon: TrendingUp,
      loading: funnelLoading,
    },
    {
      label: "Avg time to hire",
      value: fmtDays(velocity?.avgTimeToHireDays),
      unit: velocity?.avgTimeToHireDays == null ? "" : "days",
      sub: "Applied → Hired duration",
      color: "#0ea5e9",
      icon: Clock,
      loading: velocityLoading,
    },
    {
      label: "Applied → interviewed",
      value: fmtPct(velocity?.appliedToInterviewRate),
      unit: velocity?.appliedToInterviewRate == null ? "" : "%",
      sub: "Reached the interview stage",
      color: "#22c55e",
      icon: UserCheck,
      loading: velocityLoading,
    },
    {
      label: "AI 'high' accepted",
      value: fmtPct(ai?.highBandAcceptanceRate),
      unit: ai?.highBandAcceptanceRate == null ? "" : "%",
      sub: "Team acted on top AI picks",
      color: "#a855f7",
      icon: Sparkles,
      loading: aiLoading,
    },
    {
      label: "Jobs needing attention",
      value: attention?.flaggedTotal ?? 0,
      unit: "",
      sub: "No applicants or stalled",
      color: "#f59e0b",
      icon: AlertTriangle,
      loading: attentionLoading,
    },
  ];

  return (
    <PermissionGuard permission={PERMISSIONS.STATISTICS_VIEW}>
      <div className="flex flex-col gap-4" style={{ zoom: 0.9 }}>
        {/* Hero KPI strip — diagnostic rates/durations, read off the
                    widget responses below rather than fetched again. */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 items-stretch">
          {kpis.map((k) => (
            <KpiTile key={k.label} {...k} />
          ))}
        </div>

        {/* Row 1: the two headline diagnostics side by side. */}
        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] gap-3 items-stretch">
          <ConversionFunnel funnel={funnel} loading={funnelLoading} />
          <VelocityPanel velocity={velocity} loading={velocityLoading} />
        </div>

        {/* Row 2: judgment + where effort leaks. */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-stretch">
          <AiBandOutcome ai={ai} loading={aiLoading} />
          <RejectionsByStage rejections={rejections} loading={rejectionsLoading} />
        </div>

        {/* Full-width actionable worklist. */}
        <JobsAttentionPanel attention={attention} loading={attentionLoading} />

        {/* Legend note for the diagnostic intent. */}
        <p className="flex items-center gap-1.5 text-[10px] text-text-secondary px-1">
          <GitCompareArrows size={11} />
          Diagnostic view — rates, durations and outliers to act on. Volume counts live on the recruitment dashboard.
        </p>
      </div>
    </PermissionGuard>
  );
};

export default AdminStatistics;
